import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";

const isDefined = (v) => v !== undefined && v !== null && String(v).trim() !== "";

const fallbackCopyText = async (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    const ok = document.execCommand("copy");
    if (!ok) {
      throw new Error("Clipboard copy failed (execCommand returned false)");
    }
  } finally {
    document.body.removeChild(textarea);
  }
};

const copyToClipboard = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  await fallbackCopyText(text);
  return true;
};

const Row = ({ label, value }) => {
  if (!isDefined(value)) return null;
  return (
    <div className="d-flex align-items-start justify-content-between gap-3 py-1 border-bottom">
      <div className="text-muted" style={{ minWidth: 150 }}>
        {label}
      </div>
      <div className="text-end" style={{ wordBreak: "break-word" }}>
        <code>{String(value)}</code>
      </div>
    </div>
  );
};

const BuildInfoModal = ({ show, onHide, loading, error, buildInfo, onRefresh }) => {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(null);

  const shareObject = useMemo(() => {
    const bi = buildInfo || {};
    // Keep a stable, support-friendly shape.
    return {
      version: bi.version,
      sha: bi.sha,
      containerRevision: bi.containerRevision,
      region: bi.region,
      timestamp: bi.timestamp,
      traceparent: bi.traceparent,
    };
  }, [buildInfo]);

  const shareText = useMemo(() => JSON.stringify(shareObject, null, 2), [shareObject]);

  const onCopy = async () => {
    setCopyError(null);
    try {
      await copyToClipboard(shareText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      setCopied(false);
      setCopyError(e);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="p-1">
      <Modal.Header closeButton>
        <Modal.Title>Build info</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="text-muted" style={{ fontSize: 13 }}>
            Shortcut: <code>Ctrl/⌘ + Alt + Shift + D</code>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-primary" onClick={onRefresh} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </Button>
            <Button variant="primary" onClick={onCopy} disabled={loading || !buildInfo}>
              {copied ? "Copied" : "Copy to clipboard"}
            </Button>
          </div>
        </div>

        {copyError ? (
          <div className="alert alert-warning mb-3" role="alert">
            Copy failed. Your browser may block clipboard access here—try selecting the JSON below and copying
            manually.
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-danger mb-3" role="alert">
            Failed to load build info. Please try again.
          </div>
        ) : null}

        <div className="border rounded p-3">
          <Row label="Version" value={buildInfo?.version} />
          <Row label="SHA" value={buildInfo?.sha} />
          <Row label="Container revision" value={buildInfo?.containerRevision} />
          <Row label="Region" value={buildInfo?.region} />
          <Row label="Timestamp" value={buildInfo?.timestamp} />
          <Row label="Last request traceparent" value={buildInfo?.traceparent} />
          {!loading && !error && !buildInfo ? (
            <div className="text-muted">No data loaded yet.</div>
          ) : null}
          {loading ? <div className="text-muted">Loading…</div> : null}
        </div>

        <div className="mt-3">
          <div className="text-muted mb-2" style={{ fontSize: 13 }}>
            Shareable payload:
          </div>
          <pre
            className="border rounded p-3 mb-0"
            style={{ maxHeight: 260, overflow: "auto", background: "#0b1020", color: "#d6deeb" }}
          >
            {shareText}
          </pre>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default BuildInfoModal;


